import { mergeSx } from "@/utils";
import {
  type ReactElement,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { SvgIcon } from "@/components/SvgIcon";
import {
  useGetMotionProfile,
  usePrevious,
  useTheme,
  useUpdateEffect,
} from "@/hooks";
import { type IconProps, type IconVariant, isDualVariantIcon } from "@/types";
import { getMotionProfileSx } from "@/utils";
import { TooltipComponentContext } from "../Tooltip/shared";
import { fetchIconContent, spinIconKeyframes } from "./shared";
import { baseIconSx } from "./styles";

export function Icon<RC extends ReactElement | undefined = undefined>({
  icon,
  sx,
  testId,
  className,
  motionProfile,
  ...props
}: IconProps<RC>) {
  const { variant, ...otherProps } =
    "variant" in props ? props : { ...props, variant: undefined };
  const motionProfileToUse = useGetMotionProfile(motionProfile);
  const theme = useTheme();
  const { triggerDomRecrawl } = useContext(TooltipComponentContext);
  const [svgContent, setSvgContent] = useState<string[] | null>(null);
  const previousIcon = usePrevious(icon);

  const mergedSx = useMemo(() => {
    return icon === "Loading"
      ? mergeSx(
          baseIconSx,
          {
            animation: `${spinIconKeyframes} ${
              motionProfileToUse === "fast" ? "0.6s" : "1.2s"
            } linear infinite`,
            transformOrigin: "50% 50%",
          },
          sx,
        )
      : mergeSx(baseIconSx, getMotionProfileSx(motionProfileToUse, theme), sx);
  }, [icon, motionProfileToUse, sx, theme]);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    fetchIconContent(icon, variant as IconVariant, signal).then((content) => {
      if (content) setSvgContent(content);
    });

    return () => {
      controller.abort("Icon component unmounted");
    };
  }, [icon, variant]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: need to trigger dom recrawl when svg content changes for tooltips
  useEffect(() => {
    triggerDomRecrawl?.();
  }, [triggerDomRecrawl, svgContent]);

  useUpdateEffect(() => {
    if (previousIcon !== icon) setSvgContent(null);
  }, [icon, previousIcon]);

  return (
    <SvgIcon
      {...otherProps}
      testId={testId}
      sx={mergedSx}
      data-icon={icon}
      data-variant={isDualVariantIcon(icon) && variant ? variant : "regular"}
      className={`${className ?? ""} Icon Icon--${icon}`}
    >
      {svgContent && (
        <g
          data-testid={`${testId}__g`}
          // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
          dangerouslySetInnerHTML={{ __html: svgContent?.join?.("") }}
        />
      )}
    </SvgIcon>
  );
}

Icon.displayName = "Icon";
